import React, { useState, useEffect, useRef } from 'react'
import { useIntl, defineMessage } from 'react-intl'
import isEmpty from 'lodash/isEmpty'
import {
  AlertBox,
  Icons,
  DynamicTag,
  Button,
  PageActionPlaceHolder,
  Spinner
} from 'msp-components'
import { PaymentInstrument, useCustomer, useNavigation } from 'msp-integrations'
import PaymentMethodDisplay from '@/components/molecules/PaymentMethodDisplay/PaymentMethodDisplay'
import { ActionCardOtherActionProps } from '@/components/molecules/ActionCard/ActionCard.types'
import ActionCard from '@/components/molecules/ActionCard/ActionCard'
import translations from './AccountPayments.constants'
import * as S from './AccountPayments.styles'
import * as T from './AccountPayments.types'

const AccountPaymentsNoPaymentMethods = () => {
  const navigate = useNavigation()
  const { formatMessage } = useIntl()

  return (
    <PageActionPlaceHolder
      heading={formatMessage(translations.placeholderHeading)}
      text={formatMessage(translations.placeholderText)}
      buttonText={formatMessage(translations.placeholderCTAText)}
      onButtonClick={() => navigate('/shop')}
    />
  )
}

const AccountPayments = ({ scrollTop }: T.AccountPaymentsProps) => {
  const alertBoxRef = useRef<AlertBox>()
  const { formatMessage } = useIntl()
  const {
    customer,
    updateCustomer,
    paymentInstruments = [],
    removeSavedPaymentInstrument
  } = useCustomer()
  const [isRemovingPayment, setIsRemovingPayment] = useState(false)
  const [isNewDefaultPayment, setIsNewDefaultPayment] = useState(false)
  const [preferredPaymentInstrument, setPreferredPaymentInstrument] =
    useState(null)
  const [isSettingPaymentByDefault, setIsSettingPaymentByDefault] =
    useState(false)
  const [paymentInstrumentToRemove, setPaymentMethodToRemove] =
    useState<PaymentInstrument | null>(null)

  const confirmPaymentRemove = async (paymentInstrumentId: string) => {
    try {
      alertBoxRef?.current?.closeAlert()
      setIsRemovingPayment(true)
      await removeSavedPaymentInstrument(paymentInstrumentId)
      setIsRemovingPayment(false)
      setPaymentMethodToRemove(null)
    } catch (error) {
      setIsRemovingPayment(false)
    }
  }

  const onRemoveSavedPayment = async (paymentInstrument: PaymentInstrument) => {
    setPaymentMethodToRemove(paymentInstrument)
    alertBoxRef?.current?.showAlert()
    return Promise.resolve()
  }

  const cancelPaymentRemove = () => {
    setPaymentMethodToRemove(null)
    setIsRemovingPayment(false)
    alertBoxRef?.current?.closeAlert()
  }

  const setAsDefaultPayment = async (paymentInstrument: PaymentInstrument) => {
    try {
      if (paymentInstrument?.paymentInstrumentId) {
        setIsSettingPaymentByDefault(true)
        await updateCustomer({
          c_preferredPaymentInstrument: paymentInstrument.paymentInstrumentId
        })
        setIsSettingPaymentByDefault(false)
        scrollTop && scrollTop()
        setIsNewDefaultPayment(true)
        setTimeout(() => {
          setIsNewDefaultPayment(false)
        }, 750)
      }
    } catch {
      setIsSettingPaymentByDefault(false)
    }
  }

  useEffect(() => {
    if (!isEmpty(customer?.c_preferredPaymentInstrument)) {
      setPreferredPaymentInstrument(customer.c_preferredPaymentInstrument)
    }
  }, [customer])

  const setAsDefaultPaymentAction: ActionCardOtherActionProps = {
    label: defineMessage({
      defaultMessage: 'Set as default',
      id: 'use_paymentInstrument_fields.label.preferred'
    }),
    className: S.PaymentSetAsDefaultActionButton,
    actionHandler: () => null
  }

  const isPreferredPaymentMethod = (paymentInstrument: PaymentInstrument) =>
    paymentInstrument.paymentInstrumentId === preferredPaymentInstrument

  return (
    <>
      <DynamicTag
        componentTagName='Headline'
        tag='h2'
        tagStyle='h6'
        className={S.PaymentsHeadline}
      >
        {formatMessage(translations.myPayments)}
      </DynamicTag>
      <div className={S.PaymentsListWrapper}>
        {paymentInstruments.length > 0 &&
          paymentInstruments.map((paymentInstrument: PaymentInstrument) => (
            <div
              className={S.PaymentItemWrapper}
              key={`paymentInstrument-${paymentInstrument.paymentInstrumentId}`}
            >
              <div
                className={`${S.PaymentItemContentWrapper}
                  ${
                    isPreferredPaymentMethod(paymentInstrument) &&
                    isNewDefaultPayment &&
                    S.PaymentItemNewDefaultPaymentWrapper
                  }
                }`}
              >
                <ActionCard
                  noSpinner
                  otherActionsPostion='before'
                  otherActions={
                    !isPreferredPaymentMethod(paymentInstrument)
                      ? [
                          {
                            ...setAsDefaultPaymentAction,
                            actionHandler: () =>
                              setAsDefaultPayment(paymentInstrument)
                          }
                        ]
                      : []
                  }
                  onRemove={() => onRemoveSavedPayment(paymentInstrument)}
                >
                  {isPreferredPaymentMethod(paymentInstrument) && (
                    <div className={S.PaymentPreferredPaymentLabel}>
                      {formatMessage(translations.preferredPayment)}
                    </div>
                  )}
                  <PaymentMethodDisplay
                    paymentInstruments={[paymentInstrument]}
                  />
                </ActionCard>
              </div>
            </div>
          ))}
        {paymentInstruments.length === 0 && <AccountPaymentsNoPaymentMethods />}
      </div>
      {(isRemovingPayment || isSettingPaymentByDefault) && (
        <Spinner fullScreen />
      )}
      <AlertBox
        ref={alertBoxRef}
        type='standard'
        headlineColor='text-black'
        title={formatMessage(translations.paymentMethodRemoveTitle)}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
      >
        <div className={S.PaymentRemoveDescription}>
          {formatMessage(translations.paymentMethodRemoveDescription)}
        </div>

        {paymentInstrumentToRemove && (
          <PaymentMethodDisplay
            paymentInstruments={[paymentInstrumentToRemove]}
          />
        )}

        <div className={S.PaymentRemoveNote}>
          {formatMessage(translations.paymentMethodRemoveNote)}
        </div>

        <div className={S.PaymentsConfirmBoxButtonsWrapper}>
          <Button
            type={'secondary'}
            variant='outline'
            className='mx-2'
            onClick={cancelPaymentRemove}
          >
            {formatMessage(translations.paymentMethodRemoveCancel)}
          </Button>
          <Button
            type='secondary'
            onClick={() =>
              confirmPaymentRemove(
                paymentInstrumentToRemove.paymentInstrumentId
              )
            }
          >
            {formatMessage(translations.paymentMethodRemoveConfirm)}
          </Button>
        </div>
      </AlertBox>
    </>
  )
}

export default AccountPayments
